import { useEffect, useState, useCallback } from 'react'
import {
  UserSkillSummary,
  UserSkillItem,
  listUsersWithSkills,
  listUserSkillItems,
} from '@/services/admin_skills'
import { LexicalMarkdownEditor } from '@/components/editor/LexicalMarkdownEditor'

export function UserSkillsTab() {
  const [users, setUsers] = useState<UserSkillSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)

  const [selectedUser, setSelectedUser] = useState<UserSkillSummary | null>(null)
  const [userItems, setUserItems] = useState<UserSkillItem[]>([])
  const [itemsLoading, setItemsLoading] = useState(false)
  const [selectedItem, setSelectedItem] = useState<UserSkillItem | null>(null)

  const loadUsers = async () => {
    setLoading(true)
    try {
      const res = await listUsersWithSkills({ page, page_size: 20 })
      setUsers(res.items || [])
      setTotalPages(res.total_pages || 1)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadUsers()
  }, [page])

  const handleSelectUser = async (user: UserSkillSummary) => {
    setSelectedUser(user)
    setSelectedItem(null)
    setUserItems([])
    setItemsLoading(true)
    try {
      const res = await listUserSkillItems(user.user_id)
      setUserItems(res?.items || [])
    } catch (err) {
      console.error('Failed to load user skills:', err)
      setUserItems([])
    } finally {
      setItemsLoading(false)
    }
  }

  // Resizable right panel
  const [rightWidth, setRightWidth] = useState(380)
  const handleResizeMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    const startX = e.clientX
    const startWidth = rightWidth
    const onMouseMove = (ev: MouseEvent) => {
      const newWidth = Math.min(Math.max(startWidth + (startX - ev.clientX), 280), 700)
      setRightWidth(newWidth)
    }
    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
  }, [rightWidth])

  return (
    <div className="admin-skills-layout" style={{ gridTemplateColumns: `220px minmax(0, 1fr) ${rightWidth}px` }}>
      {/* Left: Users */}
      <div className="admin-skills-panel">
        <div className="admin-skills-panel-header">
          <h3>Users</h3>
        </div>
        <div className="admin-skills-panel-body">
          {loading && <div className="admin-loading">Loading...</div>}
          {!loading && users.length === 0 && (
            <div className="admin-empty">No users with skills yet.</div>
          )}
          {users.map(user => (
            <div
              key={user.user_id}
              className={`admin-skills-user-item ${selectedUser?.user_id === user.user_id ? 'active' : ''}`}
              onClick={() => handleSelectUser(user)}
            >
              <div className="admin-skills-user-info">
                <div className="admin-skills-user-name">
                  {user.username || user.email || user.user_id.slice(0, 8)}
                </div>
                {user.email && user.username && (
                  <div className="admin-skills-user-email">{user.email}</div>
                )}
              </div>
              <span className="admin-skills-user-count">{user.item_count}</span>
            </div>
          ))}
        </div>
        {totalPages > 1 && (
          <div className="admin-skills-pagination">
            <button
              className="admin-btn admin-btn--sm"
              disabled={page === 1}
              onClick={() => setPage(p => p - 1)}
            >
              Prev
            </button>
            <span>{page} / {totalPages}</span>
            <button
              className="admin-btn admin-btn--sm"
              disabled={page === totalPages}
              onClick={() => setPage(p => p + 1)}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Middle: User's Items */}
      <div className="admin-skills-panel">
        <div className="admin-skills-panel-header">
          <h3>
            {selectedUser
              ? `${selectedUser.username || selectedUser.email || 'User'}'s Skills`
              : 'Select a user'}
          </h3>
        </div>
        <div className="admin-skills-panel-body">
          {itemsLoading && <div className="admin-loading">Loading...</div>}
          {!itemsLoading && !selectedUser && (
            <div className="admin-empty">
              <div style={{ textAlign: 'center' }}>
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ opacity: 0.3, marginBottom: 10 }}>
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
                <div>Select a user to view their skills</div>
              </div>
            </div>
          )}
          {!itemsLoading && selectedUser && userItems.length === 0 && (
            <div className="admin-empty">This user has no skills.</div>
          )}
          {userItems.map(item => (
            <div
              key={item.id}
              className={`admin-skills-skill-item ${selectedItem?.id === item.id ? 'active' : ''}`}
              onClick={() => setSelectedItem(item)}
            >
              <div className="admin-skills-skill-left">
                <div className="admin-skills-skill-name">
                  {item.name}
                  {item.is_system_default && (
                    <span className="admin-badge admin-badge--system">System</span>
                  )}
                </div>
                {item.summary && (
                  <div className="admin-skills-skill-summary">{item.summary}</div>
                )}
                {item.category_name && (
                  <div className="admin-skills-skill-meta">
                    <span className="admin-skills-skill-tag">{item.category_name}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Right: Detail */}
      <div className="admin-skills-panel admin-skills-panel--right">
        <div className="admin-skills-resize-handle" onMouseDown={handleResizeMouseDown} />
        {selectedItem ? (
          <>
            <div className="admin-skills-detail-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div className="admin-skills-detail-title">{selectedItem.name}</div>
                {selectedItem.is_system_default && (
                  <span className="admin-badge admin-badge--system">System Default</span>
                )}
              </div>
              <div className="admin-skills-detail-subtitle">{selectedItem.category_name}</div>
            </div>
            <div className="admin-skills-detail-body">
              {selectedItem.summary && (
                <div className="admin-skills-detail-summary">
                  {selectedItem.summary}
                </div>
              )}
              <div className="admin-skills-editor-fill">
                <LexicalMarkdownEditor
                  key={selectedItem.id}
                  initialMarkdown={selectedItem.content_md || ''}
                  onMarkdownChange={() => {}}
                  readOnly
                />
              </div>
            </div>
          </>
        ) : (
          <div className="admin-empty" style={{ flex: 1 }}>
            <div style={{ textAlign: 'center' }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ opacity: 0.3, marginBottom: 12 }}>
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
              </svg>
              <div>Select a skill to preview</div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
